import { useState, useMemo, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { useTheme } from 'foundations-theming/utils';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { Select } from 'foundations-components/transitional/components/Select';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { H2 } from 'foundations-components/transitional/components/H2';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useIsPillBorderRadius } from 'portals-platform-lib';
import { useGetReceiptEmailRecipientsQuery, useResendReceiptEmailMutation } from '../../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/PublicBillingActionsRpc.hooks';
import { trackInteraction, trackPageView } from '../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function SendReceiptModal({
  onClose,
  commercePaymentCrmId
}) {
  var _ref, _emailOptions$;
  useEffect(() => {
    trackPageView('send-receipt-modal');
  }, []);
  const theme = useTheme();
  const isPill = useIsPillBorderRadius();
  const modalTheme = useMemo(() => {
    if (!isPill) return theme;
    return Object.assign({}, theme, {
      borderRadius: Object.assign({}, theme.borderRadius, {
        'transitional-floating': theme.borderRadius['0'],
        '0': '0px'
      })
    });
  }, [isPill, theme]);
  const {
    data,
    loading
  } = useGetReceiptEmailRecipientsQuery({
    variables: {
      commercePaymentCrmId
    }
  });
  const emailOptions = useMemo(() => {
    var _data$recipientEmails;
    return ((_data$recipientEmails = data === null || data === void 0 ? void 0 : data.recipientEmails) !== null && _data$recipientEmails !== void 0 ? _data$recipientEmails : []).map(email => ({
      text: email,
      value: email
    }));
  }, [data]);
  const [userSelectedEmail, setSelectedEmail] = useState(null);
  const selectedEmail = (_ref = userSelectedEmail !== null && userSelectedEmail !== void 0 ? userSelectedEmail : (_emailOptions$ = emailOptions[0]) === null || _emailOptions$ === void 0 ? void 0 : _emailOptions$.value) !== null && _ref !== void 0 ? _ref : null;
  const hasOneOption = emailOptions.length === 1;
  const [resendReceiptEmail, {
    loading: sending
  }] = useResendReceiptEmailMutation({
    onCompleted: () => {
      trackInteraction('send-receipt-success');
      FloatingAlertStore.addAlert({
        message: I18n.text('paymentHistory.sendReceiptModal.successMessage', {
          email: selectedEmail
        }),
        type: 'success'
      });
      onClose();
    },
    onError: () => {
      trackInteraction('send-receipt-error');
      FloatingAlertStore.addAlert({
        message: I18n.text('paymentHistory.sendReceiptModal.errorMessage', {
          email: selectedEmail
        }),
        type: 'danger'
      });
    }
  });
  const handleSend = () => {
    if (!selectedEmail) {
      return;
    }
    trackInteraction('send-receipt-initiated');
    void resendReceiptEmail({
      variables: {
        commercePaymentCrmId,
        toEmailAddress: selectedEmail
      }
    });
  };
  return /*#__PURE__*/_jsx(ThemeProvider, {
    theme: modalTheme,
    children: /*#__PURE__*/_jsxs(Modal, {
      width: 480,
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "paymentHistory.sendReceiptModal.title"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onClose
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "paymentHistory.sendReceiptModal.label"
          }),
          children: /*#__PURE__*/_jsx(Select, {
            options: emailOptions,
            value: selectedEmail,
            onChange: ({
              target: {
                value
              }
            }) => setSelectedEmail(value),
            readOnly: hasOneOption,
            disabled: loading,
            isLoading: loading
          })
        })
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          use: "primary",
          onClick: handleSend,
          disabled: !selectedEmail,
          loading: sending,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "paymentHistory.sendReceiptModal.sendButton"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary-ghost",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "paymentHistory.sendReceiptModal.closeButton"
          })
        })]
      })]
    })
  });
}